/**
 * One-off mirror of the dealer 10% Instant Rebate Offer sheet, rendered as
 * HTML so it can inherit the design system and screen-reader semantics
 * instead of being embedded as a screenshot.
 *
 * Self-contained on purpose — the whole feature is experimental and may
 * be thrown away. Buckets, labels, and T&Cs all live here so deletion is
 * one folder away.
 *
 * Several of the design's groupings (range fuel type, French Door build
 * style, refrigeration-vs-cooking accessory split) aren't expressed in
 * the engine schema. Per direction, those are derived locally here via
 * SKU-prefix and name-substring heuristics rather than by adding tags to
 * the catalog. If the schema ever grows those tags, replace the
 * predicates below with role/tag reads.
 */
import React from "react";
import { t } from "ttag";

import { skuCatalog } from "../engine";
import type {
    DiscountRole,
    ProductCategory,
    SKU,
    StructuralTag,
} from "../engine";

import styles from "./RebateBreakdown.module.scss";

// ---------- Local SKU classification ----------

const matchesPrefix = (sku: SKU, ...prefixes: string[]) =>
    prefixes.some((p) => sku.startsWith(p));

const isDualFuelRange = (sku: SKU) => matchesPrefix(sku, "SKSDR");
const isInductionRange = (sku: SKU) => matchesPrefix(sku, "SKSIR");
const isGasRange = (sku: SKU) => matchesPrefix(sku, "SKSGR", "UPSG");

/**
 * Built-in French Door = SKSFD-prefix AND name contains "Built-in".
 * Catches the four 48"/36" built-in panel-ready/pro/transitional units
 * but excludes SKSFD3623SD (Counter-Depth) and SKSSB4202S (Side-by-Side),
 * which fall to the second group.
 */
const isBuiltInFrenchDoor = (sku: SKU) =>
    matchesPrefix(sku, "SKSFD") && skuCatalog[sku].name.includes("Built-in");

const isRefrigerationAccessory = (sku: SKU) =>
    matchesPrefix(sku, "SKSPK", "SKSWK", "SKSUK", "SKSFJ");

const isCookingAccessory = (sku: SKU) =>
    matchesPrefix(sku, "SKSBG", "SKSDC", "MK");

// ---------- SKU bucket assembly ----------

const allSkus = Object.keys(skuCatalog) as SKU[];
const where = (predicate: (sku: SKU) => boolean) => allSkus.filter(predicate);
const hasRole = (sku: SKU, role: DiscountRole) =>
    skuCatalog[sku].roles.includes(role);
const inCategory = (sku: SKU, c: ProductCategory) =>
    skuCatalog[sku].category === c;
const hasTag = (sku: SKU, tg: StructuralTag) =>
    skuCatalog[sku].tags?.includes(tg) ?? false;

const buckets = {
    rangesDualFuel: where(
        (s) => hasRole(s, "qualifying_range") && isDualFuelRange(s),
    ),
    rangesInduction: where(
        (s) => hasRole(s, "qualifying_range") && isInductionRange(s),
    ),
    rangesGas: where((s) => hasRole(s, "qualifying_range") && isGasRange(s)),
    rangetops: where(
        (s) =>
            hasRole(s, "qualifying_cooktop_or_rangetop") &&
            inCategory(s, "range_top"),
    ),
    cooktops: where(
        (s) =>
            hasRole(s, "qualifying_cooktop_or_rangetop") &&
            inCategory(s, "cooktop"),
    ),
    builtInOvens: where((s) => hasRole(s, "qualifying_wall_oven")),
    builtInFrenchDoor: where(
        (s) => hasRole(s, "qualifying_fd_or_sxs") && isBuiltInFrenchDoor(s),
    ),
    frenchDoorAndSxs: where(
        (s) => hasRole(s, "qualifying_fd_or_sxs") && !isBuiltInFrenchDoor(s),
    ),
    columnRefrigerators: where((s) =>
        hasRole(s, "qualifying_column_refrigerator"),
    ),
    columnFreezers: where((s) => hasRole(s, "qualifying_column_freezer")),
    wineColumns: where(
        (s) => hasRole(s, "additional_eligible") && hasTag(s, "column_wine"),
    ),
    undercounter: where(
        (s) =>
            hasRole(s, "additional_eligible") &&
            !hasTag(s, "column_wine") &&
            (inCategory(s, "refrigerator") ||
                inCategory(s, "wine_refrigerator")),
    ),
    dishwashers: where(
        (s) => hasRole(s, "additional_eligible") && inCategory(s, "dishwasher"),
    ),
    microwaves: where(
        (s) => hasRole(s, "additional_eligible") && inCategory(s, "microwave"),
    ),
    hoods: where(
        (s) => hasRole(s, "additional_eligible") && inCategory(s, "hood"),
    ),
    refrigerationAccessories: where(isRefrigerationAccessory),
    cookingAccessories: where(isCookingAccessory),
};

// ---------- Render helpers ----------

const SkuItem = ({ sku }: { sku: SKU }) => (
    <li className={styles.skuItem}>
        <span className={styles.skuCheckbox} aria-hidden="true" />
        <span className={styles.skuCode}>{sku}</span>
    </li>
);

const SkuList = ({ skus }: { skus: SKU[] }) =>
    skus.length === 0 ? null : (
        <ul className={styles.skuList}>
            {skus.map((sku) => (
                <SkuItem key={sku} sku={sku} />
            ))}
        </ul>
    );

const SubGroup = ({ label, skus }: { label: string; skus: SKU[] }) =>
    skus.length === 0 ? null : (
        <div className={styles.subGroup}>
            <h5 className={styles.subGroupLabel}>{label}</h5>
            <SkuList skus={skus} />
        </div>
    );

const Group = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className={styles.group}>
        <h4 className={styles.groupTitle}>{label}</h4>
        {children}
    </div>
);

// ---------- Component ----------

export const RebateBreakdown = () => (
    <>
        <h3 className={styles.heading}>
            {t`Eligible appliances and accessories`}
        </h3>

        {/* ============== Cooking Set ============== */}
        <p className={styles.sectionEyebrow}>{t`Cooking Set`}</p>
        <div className={styles.boxRow}>
            <div className={styles.box}>
                <Group label={t`Ranges`}>
                    <div className={styles.subGroupColumns}>
                        <SubGroup
                            label={t`Dual Fuel`}
                            skus={buckets.rangesDualFuel}
                        />
                        <div className={styles.subGroupStack}>
                            <SubGroup
                                label={t`Induction`}
                                skus={buckets.rangesInduction}
                            />
                            <SubGroup label={t`Gas`} skus={buckets.rangesGas} />
                        </div>
                    </div>
                </Group>
            </div>
            <div className={styles.box}>
                <div className={styles.boxColumns}>
                    <div>
                        <Group label={t`Rangetops`}>
                            <SkuList skus={buckets.rangetops} />
                        </Group>
                        <div className={styles.orDivider}>
                            <span>{t`or`}</span>
                        </div>
                        <Group label={t`Cooktops`}>
                            <SkuList skus={buckets.cooktops} />
                        </Group>
                    </div>
                    <Group label={t`Built-in Ovens`}>
                        <SkuList skus={buckets.builtInOvens} />
                    </Group>
                </div>
            </div>
        </div>

        {/* ============== Refrigeration Set ============== */}
        <p className={styles.sectionEyebrow}>{t`Refrigeration Set`}</p>
        <div className={styles.boxRow}>
            <div className={styles.box}>
                <Group label={t`French Door and Side-by-Side Refrigerators`}>
                    <div className={styles.subGroupColumns}>
                        <SubGroup
                            label={t`Built-in French Door`}
                            skus={buckets.builtInFrenchDoor}
                        />
                        <SubGroup
                            label={t`French Door and Side-by-Side`}
                            skus={buckets.frenchDoorAndSxs}
                        />
                    </div>
                </Group>
            </div>
            <div className={styles.box}>
                <Group label={t`Integrated Column Refrigerators`}>
                    <div className={styles.subGroupColumns}>
                        <SubGroup
                            label={t`Column Refrigerators`}
                            skus={buckets.columnRefrigerators}
                        />
                        <SubGroup
                            label={t`Column Freezers`}
                            skus={buckets.columnFreezers}
                        />
                    </div>
                </Group>
            </div>
        </div>

        {/* ============== 1 Additional Appliance ============== */}
        <p className={styles.sectionEyebrow}>{t`1 Additional Appliance`}</p>
        <div className={styles.box}>
            <div className={styles.fourColumns}>
                <Group label={t`Refrigeration`}>
                    <SubGroup
                        label={t`Integrated Column Wine Refrigerators`}
                        skus={buckets.wineColumns}
                    />
                    <SubGroup
                        label={t`Undercounter Refrigerators`}
                        skus={buckets.undercounter}
                    />
                </Group>
                <Group label={t`Dishwashers`}>
                    <SkuList skus={buckets.dishwashers} />
                </Group>
                <Group label={t`Microwaves`}>
                    <SkuList skus={buckets.microwaves} />
                </Group>
                <Group label={t`Hoods`}>
                    <SkuList skus={buckets.hoods} />
                </Group>
            </div>
        </div>

        {/* ============== Accessories ============== */}
        <p className={styles.sectionEyebrow}>{t`Accessories`}</p>
        <div className={styles.box}>
            <div className={styles.accessoriesLayout}>
                <Group label={t`Refrigeration Accessories`}>
                    <SkuList skus={buckets.refrigerationAccessories} />
                </Group>
                <Group label={t`Cooking Accessories`}>
                    <SkuList skus={buckets.cookingAccessories} />
                </Group>
            </div>
        </div>

        {/* ============== Terms ============== */}
        <div className={styles.terms}>
            <h4 className={styles.termsHeading}>{t`* Terms and Conditions`}</h4>
            <p>
                {t`End-user consumers who purchase a package of qualifying new SKS appliances at participating retailers in the 50 U.S. states, District of Columbia, or Puerto Rico during the Program Period (defined below) will be eligible to claim a 10% instant rebate subject to the terms and conditions below. Package must include 1 (one) Cooking Set or 1 (one) Refrigeration Set plus 1 (one) more eligible product to qualify for instant rebate. Cooking Set is defined as Rangetop/Cooktop + Wall Oven or a Range. Refrigerations Set is defined as an Integrated Column Refrigerator + Integrated Freezer, or a Built-in French Door Refrigerator, Side-by-Side Refrigerator, or Counter-Depth French Door Refrigerator. Total instant rebate amount will include all purchased SKS appliances, including select accessories, excluding tax. For the purposes of this promotional offer, "bundle of qualifying new SKS appliances" refers to all new in-box appliances. This offer is subject to availability.`}
            </p>
            <p>
                {t`Except as otherwise required by law, this promotional offer is limited to one instant rebate per household. Qualifying new SKS appliances must be purchased between February 1, 2026 and December 31, 2026 (the "Program Period"). Purchase date is determined by date printed on invoice or receipt. Purchases of qualifying new SKS appliances during the Program Period must appear on the same invoice or receipt. This promotional offer is not valid in combination with the Preferred Builder Pricing Program (RBN).`}
            </p>
            <p>
                {t`Participation is at the retailer's discretion. Not all retailers will participate in this offer. See your local retailer for more information. Purchased merchandise must not be returned or else instant rebate will be subject to forfeiture. Promotional offer void where prohibited, taxed or restricted by law. Online purchases from internet auction sites or purchases of refurbished or reconditioned merchandise are not eligible for this promotional offer. Promotional offer is not valid on free, giveaway, or other complimentary promotional merchandise. Valid only in the fifty (50) states, D.C. and P.R. LG Electronics U.S.A., Inc. reserves the right to modify the terms of the promotion, including but not limited to the qualifying appliances or the authorized retailers without notice, with future effect, at any time.`}
            </p>
            <p className={styles.copyright}>
                {t`Copyright 2026© LG Electronics USA, Inc., 111 Sylvan Ave., Englewood Cliffs, NJ 07632. All rights reserved. "SKS" and the SKS logo are registered trademarks of LG Electronics Inc.`}
            </p>
        </div>
    </>
);
